// services
import { Http, HttpPromiseWithEntireResponse } from "@shared/svelte/Modules/ApiService";

// stores
import { InvoiceStore } from "@retailer/stores/InvoiceStore";

// types
import type { HTTPParams } from "@shared/types/HTTP";
import type { Invoice } from "@shared/types/Invoice";

// constants
const baseUrl = "/api/retailer/invoices";

// functions
const extractPagination = (response, pageNum) => {
  let perPage, totalEntries;
  for (let pair of response.headers.entries()) {
    const key = pair[0];
    switch (key) {
      case "x-pagination-limit":
        perPage = parseInt(pair[1]);
        break;
      case "x-pagination-entries":
        totalEntries = parseInt(pair[1]);
        break;
    }
  }

  return {
    page: Number(pageNum || 1),
    perPage: perPage,
    totalEntries: totalEntries,
  };
};

const makeReq = async (requestData: HTTPParams, successCB) => {
  // callbacks
  const errCB = () =>
    InvoiceStore.update(state => ({
      ...state,
      invoices: [],
      loaded: true,
      failed: true,
    }));

  await Http(requestData, successCB, errCB);
};
export const setLoadedState = loadedStatus => {
  InvoiceStore.update(state => ({
    ...state,
    loaded: loadedStatus,
    failed: false,
  }));
};

// requests
// --------
// get invoices index
export const index = async (
  status: string,
  currentSearchValue: string,
  filters: any,
  pageNum?: number,
) => {
  setLoadedState(false);

  // request payload
  const urlParams = new URLSearchParams({
    status: status,
    page: (pageNum || 1).toString(),
    ...(currentSearchValue && { search: currentSearchValue }),
    ...(filters.startDate && { start_date: filters.startDate }),
    ...(filters.endDate && { end_date: filters.endDate }),
    ...(filters.order && { order: filters.order }),
    ...(filters.direction && { direction: filters.direction }),
    ...(filters.type && { type: filters.type }),
    ...(filters.distributor_ids && { distributor_ids: filters.distributor_ids }),
  });

  const requestData = {
    method: "GET",
    url: baseUrl + "?" + urlParams,
  };

  // request
  const response = await HttpPromiseWithEntireResponse(requestData);

  // callbacks
  if (response.ok) {
    const json = await response.json();
    const pagination = extractPagination(response, pageNum);
    InvoiceStore.update(state => ({
      ...state,
      invoices: json,
      loaded: true,
      failed: false,
      pagination: pagination,
    }));
  } else {
    InvoiceStore.update(state => ({
      ...state,
      loaded: true,
      failed: true,
    }));
  }
};

// get invoice
export const get = async (invoiceId: number) => {
  setLoadedState(false);

  // request payload
  const requestData = {
    method: "GET",
    url: baseUrl + `/${invoiceId}`,
  };

  // callbacks
  const successCB = json =>
    InvoiceStore.update(state => ({
      ...state,
      invoice: json,
      loaded: true,
      failed: false,
    }));

  await makeReq(requestData, successCB);
};

// get invoice credits
export const getAvailableCredits = async () => {
  // request payload
  const requestData = {
    method: "GET",
    url: baseUrl + "/credits",
  };

  // callbacks
  const successCB = json =>
    InvoiceStore.update(state => ({
      ...state,
      credits: {
        json,
      },
    }));

  await makeReq(requestData, successCB);
};

// get invoice counts
export const counts = async () => {
  // request payload
  const requestData = {
    method: "GET",
    url: baseUrl + "/counts",
  };

  // callbacks
  const successCB = json =>
    InvoiceStore.update(state => ({
      ...state,
      counts: {
        paid: json.paid,
        unpaid: json.unpaid,
        credits: json.credits,
      },
    }));

  await makeReq(requestData, successCB);
};

export const getDisplayAmount = (invoice: Invoice): string => {
  if (invoice.invoice_status_type == "closed") return invoice.original_amount_due;
  return invoice.amount_due;
};
